import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle, X, AlertCircle, Loader } from 'lucide-react';
import { SubscriptionService } from '../../lib/subscription-service';

interface PaymentCompletionBannerProps {
  userId: string;
  onSubscriptionComplete?: (planId: string) => void;
}

export function PaymentCompletionBanner({ userId, onSubscriptionComplete }: PaymentCompletionBannerProps) {
  const [showBanner, setShowBanner] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [message, setMessage] = useState('');
  const [status, setStatus] = useState<'info' | 'success' | 'error'>('info');

  useEffect(() => {
    // Check if there's a pending payment message
    const pendingMessage = SubscriptionService.getPendingSubscriptionMessage();
    if (pendingMessage) {
      setMessage(pendingMessage);
      setShowBanner(true);
      setStatus('info');
      
      // Auto-check for completed subscription (no user click needed)
      setTimeout(() => {
        handleCompleteSubscription();
      }, 3000); // Give webhook 3 seconds to process
    }

    // Listen for storage events (when payment window updates the message)
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === 'rulevia_payment_message' && event.newValue) {
        setMessage(event.newValue);
        setShowBanner(true);
        setStatus('info');
        
        // Auto-check for completed subscription
        setTimeout(() => {
          handleCompleteSubscription();
        }, 3000);
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  const handleCompleteSubscription = async () => {
    setIsProcessing(true);
    console.log('🔄 PaymentCompletionBanner: Starting subscription completion for user:', userId);
    
    try {
      const result = await SubscriptionService.checkAndCompletePendingSubscription(userId);
      console.log('📋 PaymentCompletionBanner: Subscription completion result:', result);
      
      if (result.success) {
        setMessage(result.message);
        setStatus('success');
        
        // Note: GTM purchase tracking is handled by PaymentSuccessPage
        if (result.planId) {
          console.log('🎯 PaymentCompletionBanner: Subscription activated for plan:', result.planId);
        }
        
        if (result.planId && onSubscriptionComplete) {
          onSubscriptionComplete(result.planId);
        }
        
        // Auto-hide banner after 5 seconds on success
        setTimeout(() => {
          setShowBanner(false);
        }, 5000);
      } else {
        console.warn('⚠️ PaymentCompletionBanner: Subscription completion failed:', result.message);
        setMessage(result.message);
        setStatus('error');
      }
    } catch (error) {
      console.error('❌ PaymentCompletionBanner: Error during subscription completion:', error);
      setMessage(`Error: ${error instanceof Error ? error.message : 'Unknown error'}`);
      setStatus('error');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDismiss = () => {
    SubscriptionService.clearPendingSubscriptionData();
    setShowBanner(false);
  };

  if (!showBanner) return null;

  const getIcon = () => {
    if (isProcessing) return <Loader className="w-5 h-5 animate-spin" />;
    if (status === 'success') return <CheckCircle className="w-5 h-5 text-green-600" />;
    if (status === 'error') return <AlertCircle className="w-5 h-5 text-red-600" />;
    return <AlertCircle className="w-5 h-5 text-blue-600" />;
  };

  const getBannerStyles = () => {
    switch (status) {
      case 'success':
        return 'bg-green-50 border-green-200 text-green-800';
      case 'error':
        return 'bg-red-50 border-red-200 text-red-800';
      default:
        return 'bg-blue-50 border-blue-200 text-blue-800';
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: -50 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -50 }}
        transition={{ duration: 0.3 }}
        className={`rounded-lg border p-4 mb-6 ${getBannerStyles()}`}
      >
        <div className="flex items-start justify-between">
          <div className="flex items-start space-x-3">
            {getIcon()}
            <div className="flex-1">
              <p className="font-medium">
                {status === 'info' && 'Complete Your Subscription'}
                {status === 'success' && 'Subscription Activated!'}
                {status === 'error' && 'Subscription Error'}
              </p>
              <p className="text-sm mt-1 opacity-90">
                {message}
              </p>
              {status === 'info' && (
                <div className="mt-3">
                  {isProcessing ? (
                    <p className="text-sm text-blue-700 font-medium">
                      Automatically activating your subscription...
                    </p>
                  ) : (
                    <button
                      onClick={handleCompleteSubscription}
                      className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
                    >
                      Activate Subscription
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
          
          <button
            onClick={handleDismiss}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </motion.div>
    </AnimatePresence>
  );
} 